import React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import AddressSearchModal from '@/components/organisms/AddressSearchModal';
import HospitalModal from '@/components/organisms/HospitalModal';
import HospitalSearchModal from '@/components/organisms/HospitalSearchModal';
import {
  useMainHospitalCreate,
  useMainHospitalUpdate,
} from '@/hooks/main/useMainHospital';
import { useAppToast } from '@/hooks/useAppToast';
import type { Hospital, HospitalFormData } from '@/pages/main/hospital/types';
import type { ConsentFormData } from '@/schemas/consentSchema';
import {
  fullWidthToHalfWidthAlphaNum,
  fullWidthToHalfWidthNumber,
} from '@/utils';
import type { HospitalModalMode } from '../types';

interface ConsentFormModalsProps {
  handleHospitalAddressSearchSelect: (result: {
    postal_code1: string;
    postal_code2: string;
    address: string;
  }) => void;
  hospital: Hospital | null;
  hospitalId?: number | null;
  hospitalModalMode: HospitalModalMode;
  isAddressSearchModalOpen: boolean;
  isHospitalModalOpen: boolean;
  isHospitalSearchModalOpen: boolean;
  setIsAddressSearchModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsHospitalModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsHospitalSearchModalOpen: Dispatch<SetStateAction<boolean>>;
  setValue: UseFormSetValue<ConsentFormData>;
  watch: UseFormWatch<ConsentFormData>;
}

export const ConsentFormModals: React.FC<ConsentFormModalsProps> = ({
  handleHospitalAddressSearchSelect,
  hospital,
  hospitalId,
  hospitalModalMode,
  isAddressSearchModalOpen,
  isHospitalModalOpen,
  isHospitalSearchModalOpen,
  setIsAddressSearchModalOpen,
  setIsHospitalModalOpen,
  setIsHospitalSearchModalOpen,
  setValue,
  watch,
}) => {
  const hospitalUpdateMutation = useMainHospitalUpdate();
  const hospitalCreateMutation = useMainHospitalCreate();
  const toast = useAppToast();

  return (
    <>
      {/* Search Modal */}
      <HospitalSearchModal
        isOpen={isHospitalSearchModalOpen}
        onClose={() => setIsHospitalSearchModalOpen(false)}
        onSelect={(h: Hospital) => {
          setValue('hospital_id', h.id, { shouldDirty: true, shouldValidate: true });
          setValue('hospital_name', h.name || '', { shouldDirty: true, shouldValidate: true });
          setValue('hospital_postal_code1', h.postal_code1 || '', {
            shouldDirty: true,
            shouldValidate: true,
          });
          setValue('hospital_postal_code2', h.postal_code2 || '', {
            shouldDirty: true,
            shouldValidate: true,
          });
          setValue('hospital_prefecture_id', h.prefecture_id ?? null, {
            shouldDirty: true,
            shouldValidate: true,
          });
          setValue('hospital_address', h.address || '', {
            shouldDirty: true,
            shouldValidate: true,
          });
          setValue('hospital_telephone', h.telephone || '', {
            shouldDirty: true,
            shouldValidate: true,
          });

          // Select first doctor by default
          const doctors = h.doctors ?? [];
          if (doctors.length > 0) {
            setValue('doctor_id', doctors[0].id, { shouldDirty: true, shouldValidate: true });
            setValue(
              'doctor_name',
              fullWidthToHalfWidthAlphaNum(fullWidthToHalfWidthNumber(doctors[0].name)),
              { shouldDirty: true, shouldValidate: true }
            );
          } else {
            setValue('doctor_id', null, { shouldDirty: true, shouldValidate: true });
            setValue('doctor_name', '', { shouldDirty: true, shouldValidate: true });
          }

          setIsHospitalSearchModalOpen(false);
        }}
      />

      {/* Maintenance Modal */}
      <HospitalModal
        isOpen={isHospitalModalOpen}
        isEdit={true}
        hospital={hospitalModalMode === 'edit' ? hospital : null}
        isNew={hospitalModalMode === 'create'}
        skipBlocker
        onClose={() => setIsHospitalModalOpen(false)}
        onSave={async (data: HospitalFormData) => {
          if (hospitalModalMode === 'create') {
            const res = await hospitalCreateMutation.mutateAsync(data);
            if (res.success && res.data) {
              const newH = res.data as Hospital;
              setValue('hospital_id', newH.id, { shouldDirty: true, shouldValidate: true });
              setValue('hospital_name', newH.name, { shouldDirty: true, shouldValidate: true });
              setValue('hospital_postal_code1', newH.postal_code1 || '', {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue('hospital_postal_code2', newH.postal_code2 || '', {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue('hospital_prefecture_id', newH.prefecture_id ?? null, {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue('hospital_address', newH.address || '', {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue('hospital_telephone', newH.telephone || '', {
                shouldDirty: true,
                shouldValidate: true,
              });

              const doctors = newH.doctors ?? [];
              if (doctors.length > 0) {
                setValue('doctor_id', doctors[0].id, { shouldDirty: true, shouldValidate: true });
                setValue(
                  'doctor_name',
                  fullWidthToHalfWidthAlphaNum(fullWidthToHalfWidthNumber(doctors[0].name)),
                  { shouldDirty: true, shouldValidate: true }
                );
              }
              toast.success('更新が正常に終了しました。');
            }
          } else if (hospitalId) {
            const res = await hospitalUpdateMutation.mutateAsync({ id: hospitalId, data });
            if (res.success && res.data) {
              const updatedH = res.data as Hospital;
              setValue('hospital_name', updatedH.name || '', {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue('hospital_postal_code1', updatedH.postal_code1 || '', {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue('hospital_postal_code2', updatedH.postal_code2 || '', {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue('hospital_prefecture_id', updatedH.prefecture_id ?? null, {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue('hospital_address', updatedH.address || '', {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue('hospital_telephone', updatedH.telephone || '', {
                shouldDirty: true,
                shouldValidate: true,
              });
              toast.success('更新が正常に終了しました。');
            }
          }
          setIsHospitalModalOpen(false);
        }}
      />

      <AddressSearchModal
        isOpen={isAddressSearchModalOpen}
        onClose={() => setIsAddressSearchModalOpen(false)}
        onSelect={handleHospitalAddressSearchSelect}
        initialPostalCode1={watch('hospital_postal_code1') || ''}
        initialPostalCode2={watch('hospital_postal_code2') || ''}
        initialAddress={
          !(watch('hospital_postal_code1') || watch('hospital_postal_code2'))
            ? (watch('hospital_address') ?? '')
            : ''
        }
        addressType="patient"
        title="住所"
        initialActiveTab={
          watch('hospital_postal_code1') ||
          watch('hospital_postal_code2') ||
          (watch('hospital_address') || '').trim()
            ? 'search'
            : 'dropdown'
        }
      />
    </>
  );
};
